import { useMemo, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { useDashboard } from '../../contexts/DashboardContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSettings } from '../../contexts/SettingsContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Plus,
  Search,
  GripVertical,
  ChevronLeft,
  ChevronRight,
  Check,
  Puzzle,
  LayoutGrid,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  translateWidgetCategory,
  translateWidgetDescription,
  translateWidgetTitle,
} from '../../utils/widgetTranslations';

function widgetIconComponent(iconName) {
  if (!iconName || typeof iconName !== 'string') return LayoutGrid;
  const pascal = iconName
    .split(/[-_]/)
    .map((part) =>
      part.length ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''
    )
    .join('');
  const Icon = LucideIcons[pascal];
  return Icon || LayoutGrid;
}

const WidgetLibrary = () => {
  const { widgetLibrary, addWidget, layout } = useDashboard();
  const { t } = useLanguage();
  const { widgetLibraryEditExpanded, updateSetting } = useSettings();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('__all__');

  const categories = Object.keys(widgetLibrary);
  const existingWidgetKeys = new Set(layout.map((item) => item.i));

  const allWidgets = useMemo(
    () =>
      categories.flatMap((cat) =>
        (widgetLibrary[cat] || []).map((w) => ({ ...w, category: cat }))
      ),
    [categories, widgetLibrary]
  );

  const filteredWidgets = useMemo(() => {
    const cat =
      selectedCategory === '__all__' ? null : selectedCategory;
    const q = search.trim().toLowerCase();
    return allWidgets.filter((w) => {
      if (cat && w.category !== cat) return false;
      if (!q) return true;
      const title = translateWidgetTitle(t, w).toLowerCase();
      const desc = translateWidgetDescription(t, w).toLowerCase();
      const raw = `${w.name || ''} ${w.description || ''}`.toLowerCase();
      return title.includes(q) || desc.includes(q) || raw.includes(q);
    });
  }, [allWidgets, selectedCategory, search, t]);

  const handleDragStart = (e, widget) => {
    const dragData = {
      widgetKey: widget.widget_key,
      defaultSize: widget.default_size || { w: 4, h: 3 },
    };
    e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
    window.currentDragData = dragData;
  };

  const setExpanded = (next) => {
    updateSetting('widgetLibraryEditExpanded', next);
  };

  const totalCount = allWidgets.length;

  if (!widgetLibraryEditExpanded) {
    return (
      <div className='citius-widget-library flex h-full max-h-[calc(100vh-4rem)] w-11 shrink-0 flex-col border-l border-border bg-card'>
        <div className='flex justify-center border-b border-border p-1'>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='h-8 w-8 shrink-0'
            onClick={() => setExpanded(true)}
            title={t('dashboard.expandWidgetLibrary')}
          >
            <ChevronLeft className='h-4 w-4' />
          </Button>
        </div>
        <div className='flex flex-1 flex-col items-center justify-center gap-1 px-0.5 py-3'>
          <span className='text-lg font-semibold tabular-nums text-foreground'>
            {totalCount}
          </span>
          <span className='text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground [writing-mode:vertical-rl] rotate-180'>
            {t('dashboard.widgetsVerticalLabel')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className='citius-widget-library flex h-full max-h-[calc(100vh-4rem)] w-[min(18rem,85vw)] shrink-0 flex-col border-l border-border bg-card'>
      <div className='flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-2.5'>
        <div className='flex min-w-0 items-center gap-2'>
          <Puzzle className='h-4 w-4 shrink-0 text-primary' aria-hidden />
          <h3 className='truncate text-sm font-semibold text-foreground'>
            {t('dashboard.widgetLibrary')}
          </h3>
        </div>
        <Button
          type='button'
          variant='outline'
          size='icon'
          className='h-8 w-8 shrink-0'
          onClick={() => setExpanded(false)}
          title={t('dashboard.collapseWidgetLibrary')}
        >
          <ChevronRight className='h-4 w-4' />
        </Button>
      </div>

      <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3'>
        <div className='relative'>
          <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
          <Input
            placeholder={t('dashboard.searchWidgets')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className='pl-9'
          />
        </div>

        <div>
          <Select
            value={selectedCategory}
            onValueChange={setSelectedCategory}
          >
            <SelectTrigger className='w-full bg-background'>
              <SelectValue placeholder={t('dashboard.allCategories')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='__all__'>{t('dashboard.allCategories')}</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {translateWidgetCategory(t, cat)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='space-y-2.5'>
          {filteredWidgets.map((widget) => {
            const isAdded = existingWidgetKeys.has(widget.widget_key);
            const Icon = widgetIconComponent(widget.icon);
            const ds = widget.default_size || {};
            const gw = ds.w ?? '—';
            const gh = ds.h ?? '—';
            const displayName = translateWidgetTitle(t, widget);
            const displayDescription = translateWidgetDescription(t, widget);
            const categoryLabel = translateWidgetCategory(t, widget.category);

            return (
              <div
                key={widget.widget_key}
                draggable={!isAdded}
                onDragStart={(e) => handleDragStart(e, widget)}
                className={cn(
                  'rounded-xl border border-border bg-background/80 p-3 shadow-sm transition-colors',
                  isAdded
                    ? 'opacity-80'
                    : 'cursor-grab hover:border-primary/40 hover:bg-accent/40'
                )}
              >
                <div className='flex gap-3'>
                  <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground'>
                    <Icon className='h-5 w-5' aria-hidden />
                  </div>
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-start justify-between gap-2'>
                      <p className='text-sm font-medium leading-tight text-foreground'>
                        {displayName}
                      </p>
                      <span className='shrink-0 text-[0.65rem] tabular-nums text-muted-foreground'>
                        {gw}×{gh}
                      </span>
                    </div>
                    {!!displayDescription && (
                      <p className='mt-1 line-clamp-2 text-xs text-muted-foreground'>
                        {displayDescription}
                      </p>
                    )}
                    <div className='mt-2 flex flex-wrap items-center justify-between gap-2'>
                      <Badge variant='secondary' className='text-[0.65rem] font-normal'>
                        {categoryLabel}
                      </Badge>
                      {isAdded ? (
                        <span className='flex items-center gap-1 text-xs font-medium text-primary'>
                          <Check className='h-3.5 w-3.5' />
                          {t('dashboard.widgetAdded')}
                        </span>
                      ) : (
                        <div className='flex items-center gap-1'>
                          <Button
                            variant='ghost'
                            size='icon'
                            className='h-7 w-7'
                            onClick={() => addWidget(widget.widget_key)}
                            title={t('dashboard.addWidget')}
                          >
                            <Plus className='h-4 w-4' />
                          </Button>
                          <GripVertical className='h-4 w-4 text-muted-foreground' aria-hidden />
                          <span className='text-[0.65rem] text-muted-foreground'>
                            {t('dashboard.dragOrClickHint')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {filteredWidgets.length === 0 && (
            <p className='py-6 text-center text-sm text-muted-foreground'>
              {t('dashboard.noWidgets')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetLibrary;
